/* eslint-disable */
// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import type { FacilityConfig } from '../../services/missionControl.js';
import { M } from './components/primitives.js';
import { TABS, type TabDef } from './data/mock-data.js';
import { canopusCommandService, type CanopusStaffStatus } from '../../services/canopusCommand.js';
import { ORTab } from './tabs/or-overview.js';
import { StaffTab } from './tabs/staff.js';
import { SurgicalInquiriesTab } from './tabs/surgical-inquiries.js';
import { HistoricalTab } from './tabs/historical.js';
import { PerformanceTab } from './tabs/performance.js';
import { TrendsTab } from './tabs/trends.js';
import { getDemoOrStatus, DEMO_SURGEONS, DEMO_STAFF } from './data/demo-data.js';
import {
  getCanopusFacilities,
  isDemoFacility as isDemoFacilityById,
} from './data/demo-facility.js';
import canopusLogoUrl from '../../assets/canopus-logo.svg';
import './canopus-command.css';

function getCanopusServerLocation(facility: FacilityConfig | null): 'NAM5' | 'EUR3' | null {
  const project = facility?.firebaseProject?.toLowerCase();
  if (project === 'uk') return 'EUR3';
  if (project === 'us' || project === 'alpha') return 'NAM5';

  const region = facility?.region?.toLowerCase();
  if (region?.includes('uk') || region?.includes('eu')) return 'EUR3';
  if (region?.includes('us')) return 'NAM5';

  return null;
}

export function CanopusCommandPage(): React.ReactElement {
  const { facilityId } = useParams<{ facilityId: string }>();
  const location = useLocation();
  const seededFacility = (location.state as { facility?: FacilityConfig } | null)?.facility ?? null;

  const [facility, setFacility] = useState<FacilityConfig | null>(seededFacility);
  const [facilityLoading, setFacilityLoading] = useState(!seededFacility);
  const [facilityMissing, setFacilityMissing] = useState(false);

  useEffect(() => {
    if (seededFacility || !facilityId) return;
    let cancelled = false;
    getCanopusFacilities()
      .then((list) => {
        if (cancelled) return;
        const match = list.find((f) => f.id === facilityId);
        if (match) setFacility(match);
        else setFacilityMissing(true);
      })
      .catch(() => {
        if (!cancelled) setFacilityMissing(true);
      })
      .finally(() => {
        if (!cancelled) setFacilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, seededFacility]);

  if (facilityMissing) {
    return <Navigate to="/canopus-command" replace />;
  }

  return <CanopusShell facility={facility} loading={facilityLoading} />;
}

function CanopusShell({
  facility,
  loading,
}: {
  facility: FacilityConfig | null;
  loading: boolean;
}) {
  const [tab, setTab] = useState('or');
  const [time, setTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(
    () => localStorage.getItem('cc-fullscreen') === 'true'
  );

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => {
      const next = !prev;
      localStorage.setItem('cc-fullscreen', String(next));
      if (next) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      } else {
        document.exitFullscreen?.().catch(() => {});
      }
      return next;
    });
  }, []);

  // Sync state when user exits fullscreen via Escape key
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        setIsFullscreen(false);
        localStorage.setItem('cc-fullscreen', 'false');
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [isFullscreen]);

  // Re-enter browser fullscreen on mount if persisted
  useEffect(() => {
    if (isFullscreen && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch live OR status once here; share between the metric strip and the
  // OR tab. 10s polling. `orStatus === null` means not-yet-loaded.
  const [orStatus, setOrStatus] = useState<any>(null);
  const [orLoading, setOrLoading] = useState(false);
  // Live staff for real facilities (Staff + SI tabs). Demo facility uses
  // the baked DEMO_STAFF pool instead — no live fetch.
  const [liveStaff, setLiveStaff] = useState<CanopusStaffStatus | null>(null);
  const facilityId = facility?.id ?? null;

  // True for the dedicated demo facility (by fixed id). Single gate for
  // demo mode — there is no toggle.
  const demoMode = useMemo(() => isDemoFacilityById(facility), [facility?.id]);
  const facilityName = facility?.facilityName ?? undefined;
  // Baked pools are only meaningful in demo mode; tabs ignore them live.
  const demoSurgeons = demoMode ? DEMO_SURGEONS : null;
  const demoStaff = demoMode ? DEMO_STAFF : liveStaff;

  useEffect(() => {
    if (!facilityId) {
      setOrStatus(null);
      setOrLoading(false);
      return;
    }
    // Demo facility: no backend. Feed baked pools + display name into the
    // synthetic OR status. Still tick every 10s so timestamps stay fresh.
    if (demoMode) {
      setOrLoading(false);
      setOrStatus(getDemoOrStatus(DEMO_SURGEONS, DEMO_STAFF, facilityName));
      const interval = setInterval(() => {
        setOrStatus(getDemoOrStatus(DEMO_SURGEONS, DEMO_STAFF, facilityName));
      }, 10000);
      return () => clearInterval(interval);
    }
    let cancelled = false;
    setOrLoading(true);
    const fetchStatus = async () => {
      try {
        const data = await canopusCommandService.getOrStatus(facilityId);
        if (!cancelled) setOrStatus(data);
      } catch {
        // Silently absorb transient errors; polling will retry.
      } finally {
        if (!cancelled) setOrLoading(false);
      }
    };
    void fetchStatus();
    const interval = setInterval(() => void fetchStatus(), 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [facilityId, demoMode, facilityName]);

  // Staff for Staff/SI tabs. Demo uses the baked pool; live facilities fetch.
  useEffect(() => {
    if (!facilityId || demoMode) {
      setLiveStaff(null);
      return;
    }
    let cancelled = false;
    canopusCommandService
      .getStaffStatus(facilityId)
      .then((data) => {
        if (!cancelled) setLiveStaff(data);
      })
      .catch(() => {
        if (!cancelled) setLiveStaff(null);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, demoMode]);

  // UK facilities use "OT" (Operating Theatre) instead of "OR" (Operating Room).
  const isUk = !!(facility?.region && /uk|eu/i.test(facility.region));
  const roomLabel = isUk ? 'OT' : 'OR';
  const roomFullLabelPlural = isUk ? 'Operating Theatres' : 'Operating Rooms';
  const serverLocation = getCanopusServerLocation(facility);

  const rooms = orStatus?.rooms ?? [];
  const activeRecordings = rooms.filter((r) =>
    ['active', 'long', 'setup', 'closing', 'new'].includes(r.status)
  ).length;
  const completedToday = orStatus
    ? (orStatus.todaysCases ?? []).filter((c) => c.endTimeIso).length
    : 0;
  const runningLong = rooms.filter((r) => r.status === 'long').length;
  const buildingAvg = rooms.filter((r) => r.status === 'new').length;
  const coveragePct = rooms.length > 0 ? Math.round((activeRecordings / rooms.length) * 100) : 0;

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className={`canopus-command cc-shell${isFullscreen ? ' cc-shell--fullscreen' : ''}`}>
      <div className="cc-shell-inner">
        {/* Page header — matches /insights page-header pattern so the transition
            from the facility list feels continuous with the rest of the app.
            The global NavBar sits above this. No more "second app" feel. */}
        <header className="cc-shell-header">
          <div className="cc-shell-header-row">
            <div className="cc-shell-eyebrow-row">
              <Link
                to="/canopus-command"
                aria-label="Back to facility picker"
                className="cc-shell-back"
              >
                <ArrowLeft size={14} />
              </Link>
              <img src={canopusLogoUrl} alt="Canopus" className="cc-shell-logo" />
              <div className="cc-shell-pulse-dot" />
              <span className={`cc-shell-title ${loading ? 'loading-blur' : ''}`.trim()}>
                {loading ? 'Facility Name' : (facility?.facilityName ?? 'Facility')}
              </span>
            </div>

            <div className="cc-shell-status">
              <div className="cc-shell-live">
                <div className="cc-shell-live-dot" />
                <span className="cc-shell-live-label">LIVE</span>
                {serverLocation && <span className="cc-shell-live-server">{serverLocation}</span>}
              </div>
              <span className="cc-shell-clock">
                {time.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
              <button
                className="cc-fullscreen-btn"
                title={isFullscreen ? 'Exit full screen' : 'Full screen'}
                onClick={toggleFullscreen}
              >
                {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            </div>
          </div>
        </header>

        {/* Body */}
        {/* Metric strip — live from orStatus */}
        <div
          className={`cc-shell-metric-strip ${orLoading && !orStatus ? 'loading-blur' : ''}`.trim()}
        >
          <M
            label="Active Recordings"
            value={activeRecordings}
            sub={`of ${rooms.length} rooms`}
            tip="Number of ORs currently being recorded by Canopus, including cases in pre-op, active surgery, or closing."
          />
          <M
            label="Completed Today"
            value={completedToday}
            color="#34D399"
            tip="Cases that finished recording today. A completed case has a full step-by-step timeline available in the Historical tab."
          />
          <M
            label="Running Long"
            value={runningLong}
            sub="vs historical avg"
            color={runningLong > 0 ? '#FBBF24' : '#0FB7D1'}
            tip="Cases currently exceeding the historical average duration for that surgeon + procedure combination."
          />
          <M
            label="Building Avg"
            value={buildingAvg}
            sub="<15 cases recorded"
            color="#6366F1"
            tip="Cases where the surgeon + procedure combination has fewer than 15 recorded cases, so no reliable historical average exists yet."
          />
          <M
            label="Canopus Coverage"
            value={`${coveragePct}%`}
            sub="Rooms with active recording"
            tip="Percentage of ORs that have an active Canopus recording right now. Higher coverage means more real-time visibility."
          />
        </div>

        {/* Tab bar */}
        <div className="cc-shell-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`cc-shell-tab ${tab === t.id ? 'cc-shell-tab--active' : ''}`.trim()}
              title={t.beta ? 'This section is in beta and may change' : undefined}
            >
              {t.live && (
                <div
                  className={`cc-shell-tab-dot ${tab === t.id ? 'cc-shell-tab-dot--active' : ''}`.trim()}
                />
              )}
              {t.id === 'or' ? `${roomLabel} Overview` : t.label}
              {t.beta && <span className="cc-tab-beta">BETA</span>}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'or' && (
          <ORTab
            orStatus={orStatus}
            isLoading={orLoading}
            roomLabel={roomLabel}
            demoMode={demoMode}
          />
        )}
        {tab === 'staff' && (
          <StaffTab
            facilityId={facility?.id ?? null}
            roomLabel={roomLabel}
            demoMode={demoMode}
            realStaff={demoStaff}
          />
        )}
        {tab === 'si' && (
          <SurgicalInquiriesTab
            facilityId={facility?.id ?? null}
            roomLabel={roomLabel}
            demoMode={demoMode}
            realStaff={demoStaff}
            realSurgeons={demoSurgeons}
          />
        )}
        {tab === 'historical' && (
          <HistoricalTab
            facilityId={facility?.id ?? null}
            roomLabel={roomLabel}
            demoMode={demoMode}
            realSurgeons={demoSurgeons}
            realStaff={demoStaff}
          />
        )}
        {tab === 'performance' && (
          <PerformanceTab
            facilityId={facility?.id ?? null}
            roomFullLabelPlural={roomFullLabelPlural}
            demoMode={demoMode}
          />
        )}
        {tab === 'trends' && (
          <TrendsTab
            facilityId={facility?.id ?? null}
            roomLabel={roomLabel}
            demoMode={demoMode}
            realSurgeons={demoSurgeons}
          />
        )}
      </div>
    </div>
  );
}
